// useAppSecrets — owner-only Secrets state for one app: the list of DECLARED secrets and
// their status (never their value), plus set/clear mutations. Same plain useState/useEffect
// + `cancelled` guard convention as useAppData (no react-query). All calls go through the
// owner-only /apps/:slug/secrets endpoints; errors surface as strings for the panel.

import { useCallback, useEffect, useState } from "react";
import { listSecrets, setSecret, clearSecret, type SecretStatus, type SecretsEnv } from "../api";

export type UseAppSecrets = {
  secrets: SecretStatus[] | null;   // null while loading
  error:   string | null;
  reload:  () => void;
  // Mutations resolve on success (list auto-refetches) or reject with the server's error
  // message (the caller shows it inline). `value` is sent to the server as-is.
  set:     (name: string, value: string) => Promise<void>;
  clear:   (name: string) => Promise<void>;
};

export function useAppSecrets(slug: string, env: SecretsEnv = "dev"): UseAppSecrets {
  const [secrets, setSecrets] = useState<SecretStatus[] | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [nonce, setNonce]     = useState(0);

  useEffect(() => {
    let cancelled = false;
    setSecrets(null); setError(null);
    listSecrets(slug, env)
      .then((r) => { if (!cancelled) setSecrets(r.secrets); })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [slug, env, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const set = useCallback(async (name: string, value: string) => { await setSecret(slug, name, value, env); setNonce((n) => n + 1); }, [slug, env]);
  const clear = useCallback(async (name: string) => { await clearSecret(slug, name, env); setNonce((n) => n + 1); }, [slug, env]);

  return { secrets, error, reload, set, clear };
}
